<template>
  <div class="editor-container">

    <div class="editor-header">
      <h3><i class="bx bx-text"></i> Text Editor</h3>
      <p>Modify selected text element</p>
    </div>

    <div v-if="!selectedElement" class="editor-empty">
      <i class="bx bx-select-multiple"></i>
      <p>No text selected</p>
    </div>

    <div v-else class="editor-body">

      <label class="form-label">Live Editing</label>
      <div
        ref="editable"
        class="edit-area"
        :style="previewStyle"
        contenteditable="true"
        @input="onTextEdit"
      ></div>

      <div class="form-group">
        <label class="form-label">Font Size ({{ size }}px)</label>
        <input class="slider" type="range" min="10" max="60" v-model="size" @input="apply" />
      </div>

      <div class="row">
        <div class="field">
          <label class="form-label">Text Color</label>
          <input type="color" class="color-input" v-model="color" @input="apply" />
        </div>
       <div class="field" v-if="canUseBackground">
        <label class="form-label">Background</label>
        <input
          type="color"
          class="color-input"
          v-model="bgColor"
          @input="apply"
        />
      </div>

      </div>

       <!-- Link Field -->
      <label class="label">Attach Link (Href)</label>
      <input type="text"
             class="file-input"
             v-model="hrefLink"
             placeholder="https://example.com or #section-id"
             @input="applyHref"
      />

      <label class="form-label">Font Weight</label>
      <select class="input-select" v-model="weight" @change="apply">
        <option value="400">Normal</option>
        <option value="500">Medium</option>
        <option value="600">Semi Bold</option>
        <option value="700">Bold</option>
      </select>

      <label class="form-label">Alignment</label>
      <div class="align-group">
        <button :class="{active: alignVal === 'left'}" @click="align('left')"><i class="bx bx-align-left"></i></button>
        <button :class="{active: alignVal === 'center'}" @click="align('center')"><i class="bx bx-align-middle"></i></button>
        <button :class="{active: alignVal === 'right'}" @click="align('right')"><i class="bx bx-align-right"></i></button>
      </div>

      <div class="action-buttons">
        <button class="btn-reset" @click="resetText"><i class="bx bx-revision"></i> Reset</button>
        <button class="btn-remove" @click="$emit('removeElement')"><i class="bx bx-trash"></i> Remove</button>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: "TextEditor",
  props: { selectedElement: Object },
  emits: ["updateText", "removeElement"],

  data() {
    return {
      size: 16,
      color: "#000000",
      bgColor: "transparent",
      weight: "400",
      alignVal: "left",
      previewText: "",
      originalText: "",
      originalStyles: {}
    };
  },

  computed: {
  previewStyle() {
    return {
      fontSize: this.size + "px",
      color: this.color,
      fontWeight: this.weight,
      textAlign: this.alignVal,
      backgroundColor: this.bgColor
    };
  },

  // 🔹 ADD THIS
  canUseBackground() {
    if (!this.selectedElement) return false;

    const tag = this.selectedElement.tagName;
    return !["INPUT", "TEXTAREA", "IMG", "BUTTON"].includes(tag);
  }
}
,

 watch: {
  selectedElement: {
    immediate: true,
    handler(el) {
      if (!el) return;

      // 🔹 FIX START
      if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
        this.previewText = el.value || el.placeholder || "";
      } else {
        this.previewText = el.innerText || "";
      }
      // 🔹 FIX END

      this.originalText = this.previewText;

      this.size = parseInt(el.style.fontSize) || this.detectFontSize(el);
      this.color = el.style.color || "#000000";
      const computedBg = window.getComputedStyle(el).backgroundColor;

      // rgba(0, 0, 0, 0) → no background
      if (!computedBg || computedBg === "rgba(0, 0, 0, 0)") {
        this.bgColor = "#ffffff"; // default white
      } else {
        this.bgColor = this.rgbToHex(computedBg);
      }

      this.weight = el.style.fontWeight || "400";
      this.alignVal = el.style.textAlign || "left";

      this.originalStyles = {
        size: this.size,
        color: this.color,
        bgColor: this.bgColor,
        weight: this.weight,
        align: this.alignVal,
      };

      if (el.closest("a")) {
        this.hrefLink = el.closest("a").getAttribute("href") || "";
      } else {
        this.hrefLink = "";
      }

      this.$nextTick(() => {
        if (this.$refs.editable) {
          this.$refs.editable.innerText = this.previewText;
          this.setCaretToEnd(this.$refs.editable);
        }
      });
    }
  }
}
,

  methods: {
    rgbToHex(rgb) {
      const result = rgb.match(/\d+/g);
      if (!result) return "#ffffff";

      return (
        "#" +
        result
          .slice(0, 3)
          .map(x => parseInt(x).toString(16).padStart(2, "0"))
          .join("")
      );
    }
    ,
    detectFontSize(el) {
      return parseInt(window.getComputedStyle(el).fontSize) || 16;
    },
       applyHref() {
  if (!this.selectedElement) return;

  let element = this.selectedElement;

  // If link exists, update it
  if (element.closest("a")) {
    element.closest("a").setAttribute("href", this.hrefLink);
    return;
  }

  // If no link, wrap element in <a>
  const wrapper = document.createElement("a");
  wrapper.href = this.hrefLink;
  wrapper.target = "_blank";
  element.replaceWith(wrapper);
  wrapper.appendChild(element);
},
    onTextEdit(e) {
      this.saveCaretPosition();
      this.previewText = e.target.innerText;

      // Emit text change (parent will handle input vs text)
      this.$emit("updateText", { text: this.previewText });

      this.restoreCaretPosition();
    },

    apply() {
      this.$emit("updateText", {
        size: this.size,
        color: this.color,
        weight: this.weight,
        bg: this.canUseBackground ? this.bgColor : null,
        align: this.alignVal
      });

    },

    align(type) {
      this.alignVal = type;
      this.$emit("updateText", { align: type });
    },

    resetText() {
      this.previewText = this.originalText;
      this.size = this.originalStyles.size;
      this.color = this.originalStyles.color;
      this.bgColor = this.originalStyles.bgColor;
      this.weight = this.originalStyles.weight;
      this.alignVal = this.originalStyles.align;

      this.$nextTick(() => {
        if (this.$refs.editable) {
          this.$refs.editable.innerText = this.previewText;
          this.setCaretToEnd(this.$refs.editable);
        }
      });

      this.apply();
      this.$emit("updateText", { text: this.previewText });
    },

    /* ---------- CURSOR POSITION FIX FUNCTIONS ---------- */

    saveCaretPosition() {
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      this.caretRange = sel.getRangeAt(0);
    },

    restoreCaretPosition() {
      if (!this.caretRange) return;
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(this.caretRange);
    },

    setCaretToEnd(el) {
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }
  
};
</script>
<style scoped>
.editor-container {
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* File Input */
.file-input {
  width: 100%;
  padding: 9px;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
}
.file-input:hover {
  border-color: #000;
}


/* Header */
.editor-header h3 {
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #111;
}
.editor-header p {
  font-size: 12px;
  color: #888;
  margin-top: -4px;
}

/* Empty Placeholder */
.editor-empty {
  border: 1px dashed #cfcfcf;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  animation: fade .25s ease;
}
.editor-empty i {
  font-size: 22px;
  margin-bottom: 6px;
  color: #777;
}
.editor-empty p {
  font-size: 13px;
  color: #999;
}

/* Body */
.editor-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: fade .25s ease;
}

/* Label */
.form-label {
  font-size: 12px;
  color: #333;
  font-weight: 500;
}

/* Editable text box */
.edit-area {
  min-height: 64px;
  max-height: 150px;
  padding: 11px;
  border: 1px solid #d6d6d6;
  border-radius: 10px;
  background: #fbfbfb;
  overflow-y: auto;
  line-height: 1.4;
  cursor: text;
  transition: .18s ease;
}
.edit-area:focus {
  border-color: #000;
  box-shadow: 0 0 0 1px #000 inset;
}

/* Range slider */
.slider {
  width: 100%;
  cursor: pointer;
  accent-color: #000;
}

/* Two columns input */
.row {
  display: flex;
  gap: 10px;
}
.color-input {
  width: 100%;
  border-radius: 8px;
  height: 38px;
  border: 1px solid #d4d4d4;
  cursor: pointer;
}

/* Select */
.input-select {
  width: 100%;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  padding: 8px;
  font-size: 13px;
  cursor: pointer;
}

/* Alignment buttons */
.align-group {
  display: flex;
  gap: 8px;
}
.align-group button {
  flex: 1;
  padding: 7px 0;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: .18s;
}
.align-group button:hover {
  background: #f0f0f0;
}
.align-group .active {
  background: #000;
  border-color: #000;
  color: #fff;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 8px;
}
.action-buttons button {
  flex: 1;
  border-radius: 8px;
  padding: 8px;
  font-size: 13px;
  display: flex;
  justify-content: center;
  gap: 5px;
  align-items: center;
  cursor: pointer;
}
.btn-reset {
  background: #f2f2f2;
  color: #000;
}
.btn-reset:hover {
  background: #e6e6e6;
}
.btn-remove {
  background: #000;
  color: #fff;
}
.btn-remove:hover {
  background: #222;
}

@keyframes fade {
  from {opacity: 0; transform: translateY(4px);}
  to {opacity: 1; transform: translateY(0);}
}
</style>
